// src/pages/creator-panel/Resubscribe.js
// Standalone resubscription page (rendered OUTSIDE the app Layout). The Layout gate
// redirects subscriptions in the LOCKED state here.
//
// Flow for team admins/owners:
//   Step 1  pick a plan from the grid
//   Step 2  the grid hides, the selected plan summary + a card section render.
//           - saved usable card on file: just a confirm button
//           - no saved card on a paid plan: Stripe card form + "Save this card"
//                                            checkbox + confirm
//           - free plan: confirm with no card needed
//   "Change plan" on Step 2 returns to Step 1 without losing card form state.
//
// Non-admin team members see a "contact your admin" message instead.
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import {
    Box, Typography, Button, Card, CardContent, CardActions, Grid, Chip,
    CircularProgress, Alert, Stack, Divider, Checkbox, FormControlLabel,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripe_publishable_key } from '../../config';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const stripePromise = stripe_publishable_key ? loadStripe(stripe_publishable_key) : null;

const formatCurrency = (amount, currency = 'usd') => {
    const n = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(n)) return '';
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency', currency: (currency || 'usd').toUpperCase(),
        }).format(n);
    } catch (e) {
        return `$${n.toFixed(2)}`;
    }
};

const logout = () => {
    localStorage.clear();
    window.location.href = '/login';
};

const Shell = ({ children }) => (
    <Box sx={{
        minHeight: '100vh', fontFamily,
        background: 'linear-gradient(135deg,#f8fafc 0%,#eef2ff 45%,#e0e7ff 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        py: { xs: 4, md: 8 }, px: 2,
    }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');`}</style>
        {children}
    </Box>
);

// POST /subscriptions. When gatewayPmId is given, save_payment_method controls whether
// the backend persists the card for future use. `interval` is 'month' or 'year' and is
// passed straight through to the API so the subscription is stamped with the right cycle.
// Returns { ok } or { ok:false, message }.
async function postResubscription(planId, gatewayPmId, saveCard, interval) {
    const body = { plan_id: planId };
    if (interval) body.interval = interval;
    if (gatewayPmId) {
        body.gateway_payment_method_id = gatewayPmId;
        body.save_payment_method = !!saveCard;
    }
    try {
        const res = await axiosInstance.post('/subscriptions', body);
        if (res.status === 200 || res.status === 201) return { ok: true };
        if (res.status === 202) {
            return { ok: false, message: 'This payment needs additional confirmation. Please try a different card.' };
        }
        return { ok: false, message: res.data?.message || 'Could not complete resubscription. Please try again.' };
    } catch (err) {
        const status = err.response?.status;
        const msg = err.response?.data?.message || err.response?.data?.error || err.message;
        if (status === 402 || (msg && /payment method|card/i.test(msg))) {
            return { ok: false, message: 'The payment could not be completed. Please check your card details and try again.' };
        }
        return { ok: false, message: msg || 'Could not complete resubscription. Please try again.' };
    }
}

const buildFeatureList = (plan) => {
    const f = plan.features || {};
    return [
        f.web_pages_limit === -1 ? 'Unlimited Web Pages'
            : f.web_pages_limit && `${f.web_pages_limit} Web Pages`,
        f.document_limit === -1 ? 'Unlimited Documents'
            : f.document_limit && `${f.document_limit} Documents`,
        f.llm_tokens_in_limit === -1 ? 'Unlimited LLM Tokens'
            : f.llm_tokens_in_limit && `${(f.llm_tokens_in_limit / 1000000).toFixed(1)}M In Tokens`,
        f.llm_tokens_out_limit === -1 ? ''
            : f.llm_tokens_out_limit && `${(f.llm_tokens_out_limit / 1000000).toFixed(1)}M Out Tokens`,
        f.multilingual && 'Multilingual Support',
        f.google_calendar && 'Google Calendar',
        f.ecommerce && 'E-commerce Features',
        f.ai_agents && 'AI Agents Access',
    ].filter(Boolean);
};

// Cycle-aware pricing helper: given a plan + the current billingCycle, returns
// the numbers the UI needs without the renderer having to know the formula:
//   - effective: the amount the user actually pays per cycle.
//   - list:      pre-discount price per cycle (only > effective when yearly).
//   - monthlyEquivalent: yearly effective / 12 (for the "$X/mo billed yearly" line).
//   - hasDiscount: whether to render the strikethrough + "Save 10%" badge.
const YEARLY_DISCOUNT_PCT = 10;
const pricingFor = (plan, billingCycle) => {
    const monthlyPrice = Number(plan?.price) || 0;
    if (monthlyPrice <= 0 || !billingCycle) {
        return { list: monthlyPrice, effective: monthlyPrice, monthlyEquivalent: null, hasDiscount: false, cycleLabel: 'month' };
    }
    if (billingCycle === 'year') {
        const list = monthlyPrice * 12;
        const effective = Math.round(list * (1 - YEARLY_DISCOUNT_PCT / 100) * 100) / 100;
        return { list, effective, monthlyEquivalent: effective / 12, hasDiscount: true, cycleLabel: 'year' };
    }
    return { list: monthlyPrice, effective: monthlyPrice, monthlyEquivalent: null, hasDiscount: false, cycleLabel: 'month' };
};

// Shared price renderer: handles freemium ('Free'), monthly ('$10 /month'),
// and yearly ('$540 /year ~$600~  Save 10%' + monthly-equivalent caption).
// Used by both the Step-1 grid card and the Step-2 selected-plan summary.
const renderPlanPrice = (plan, billingCycle, sx = {}) => {
    const isFree = Number(plan.price) === 0;
    if (isFree) {
        return (
            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.4rem', color: '#3b82f6', ...sx }}>
                Free
            </Typography>
        );
    }
    const p = pricingFor(plan, billingCycle);
    if (p.hasDiscount) {
        return (
            <Box sx={sx}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.4rem', color: '#3b82f6' }}>
                        {formatCurrency(p.effective, plan.currency)}
                        <Typography component="span" sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                            {' '}/{p.cycleLabel}
                        </Typography>
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8', textDecoration: 'line-through' }}>
                        {formatCurrency(p.list, plan.currency)}
                    </Typography>
                </Box>
                {p.monthlyEquivalent && (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                        Equivalent to {formatCurrency(p.monthlyEquivalent, plan.currency)} / month
                    </Typography>
                )}
                <Chip
                    size="small" color="success" label={`Save ${YEARLY_DISCOUNT_PCT}%`}
                    sx={{ fontFamily, mt: 0.5, height: 20, fontWeight: 600 }}
                />
            </Box>
        );
    }
    return (
        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.4rem', color: '#3b82f6', ...sx }}>
            {formatCurrency(p.effective, plan.currency)}
            <Typography component="span" sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                {' '}/{p.cycleLabel}
            </Typography>
        </Typography>
    );
};

// --- Step 1: plan grid. Clicking a card selects the plan; the actual charge
// happens on Step 2 after the user confirms / fills the card form. ---
// `billingCycle` / `onCycleChange` drive the Monthly/Yearly toggle rendered
// above the grid. The SAME plans show in both modes -- only the displayed
// price changes (monthly vs yearly with 10% off baked in).
const PlanGrid = ({ plans, onSelect, billingCycle, onCycleChange }) => {
    const anyPaidPlan = plans.some(p => Number(p.price) > 0);
    return (
        <>
            {anyPaidPlan && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                    <ToggleButtonGroup
                        value={billingCycle}
                        exclusive
                        onChange={(_, val) => { if (val) onCycleChange(val); }}
                        size="small"
                        color="primary"
                        aria-label="billing cycle"
                        sx={{ bgcolor: 'rgba(255,255,255,0.7)', borderRadius: '14px' }}
                    >
                        <ToggleButton value="month" sx={{ fontFamily, textTransform: 'none', px: 3, fontWeight: 600 }}>
                            Monthly
                        </ToggleButton>
                        <ToggleButton value="year" sx={{ fontFamily, textTransform: 'none', px: 3, fontWeight: 600 }}>
                            Yearly
                            <Chip size="small" color="success" label={`Save ${YEARLY_DISCOUNT_PCT}%`}
                                sx={{ fontFamily, ml: 1, height: 20, fontWeight: 600 }} />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}
            <Grid container spacing={2.5} justifyContent="center" alignItems="stretch">
                {plans.length === 0 && (
                    <Grid item xs={12}>
                        <Typography sx={{ fontFamily, textAlign: 'center', color: '#64748b' }}>
                            No plans available.
                        </Typography>
                    </Grid>
                )}
                {plans.map((plan) => {
                    const isFree = Number(plan.price) === 0;
                    const featureList = buildFeatureList(plan);
                    return (
                        <Grid item key={plan.id} xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                            <Card sx={{
                                width: '100%', borderRadius: '16px', display: 'flex',
                                flexDirection: 'column', justifyContent: 'space-between',
                                border: '1px solid rgba(0,0,0,0.08)',
                            }}>
                                <CardContent>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>
                                        {plan.display_name}
                                    </Typography>
                                    {renderPlanPrice(plan, billingCycle, { my: 1 })}
                                    {isFree && <Chip label="Lifetime free access" size="small" color="success" sx={{ fontFamily }} />}
                                    <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 1.5, mb: 0 }}>
                                        {featureList.map((feature, idx) => (
                                            <Typography
                                                component="li" key={idx}
                                                sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', mb: 0.5 }}
                                            >
                                                ✓ {feature}
                                            </Typography>
                                        ))}
                                        {featureList.length === 0 && (
                                            <Typography component="li" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                Basic Features
                                            </Typography>
                                        )}
                                    </Box>
                                </CardContent>
                                <CardActions sx={{ p: 2, pt: 0 }}>
                                    <Button
                                        fullWidth variant={isFree ? 'outlined' : 'contained'}
                                        onClick={() => onSelect(plan)}
                                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                                    >
                                        {isFree ? 'Select Freemium' : 'Select Plan'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );
};

// Shared header for Step 2: the chosen plan + a "Change plan" back link.
// `billingCycle` decides whether the summary shows the monthly or yearly price.
const SelectedPlanSummary = ({ plan, billingCycle, onBack }) => {
    const featureList = buildFeatureList(plan);
    return (
        <>
            <Button
                onClick={onBack} startIcon={<ArrowBackIcon />}
                sx={{ fontFamily, textTransform: 'none', mb: 1.5, color: '#475569' }}
            >
                Change plan
            </Button>
            <Card sx={{ borderRadius: '16px', mb: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
                <CardContent>
                    <Typography sx={{
                        fontFamily, fontSize: '0.72rem', fontWeight: 700,
                        letterSpacing: 0.6, textTransform: 'uppercase', color: '#64748b',
                    }}>
                        Selected plan
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt: 0.5, gap: 2 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.2rem', color: '#1e293b' }}>
                            {plan.display_name}
                        </Typography>
                        <Box sx={{ textAlign: 'right' }}>
                            {renderPlanPrice(plan, billingCycle)}
                        </Box>
                    </Stack>
                    {featureList.length > 0 && (
                        <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 1.5, mb: 0 }}>
                            {featureList.map((feature, idx) => (
                                <Typography
                                    component="li" key={idx}
                                    sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', mb: 0.5 }}
                                >
                                    ✓ {feature}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </>
    );
};

// --- Step 2 (saved card OR free plan): a single confirm button ------------------
const SavedCardCheckout = ({ plan, savedCard, billingCycle, onBack }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const isFree = Number(plan.price) <= 0;
    const canConfirm = isFree || !!savedCard;

    const handleConfirm = async () => {
        if (busy || !canConfirm) return;
        setBusy(true);
        setError(null);
        try {
            const r = await postResubscription(plan.id, null, null, isFree ? 'month' : billingCycle);
            if (r.ok) { window.location.href = '/chatbots'; return; }
            setError(r.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box>
            <SelectedPlanSummary plan={plan} billingCycle={billingCycle} onBack={onBack} />
            {isFree ? (
                <Alert severity="success" sx={{ mb: 2, fontFamily }}>
                    No payment required for this plan. You'll keep lifetime free access.
                </Alert>
            ) : savedCard ? (
                <Alert severity="info" sx={{ mb: 2, fontFamily }}>
                    This plan will be charged to your saved {(savedCard.card_brand || 'card').toUpperCase()} ****{savedCard.last4 || ''}.
                    {' '}
                    <Button
                        size="small"
                        onClick={() => { window.location.href = '/Billing?tab=cards'; }}
                        sx={{ fontFamily, textTransform: 'none' }}
                    >
                        Manage payment methods
                    </Button>
                </Alert>
            ) : (
                <Alert severity="warning" sx={{ mb: 2, fontFamily }}>
                    Add a payment method to resubscribe to a paid plan, or pick the free plan instead.
                    {' '}
                    <Button
                        size="small"
                        onClick={() => { window.location.href = '/Billing?tab=cards'; }}
                        sx={{ fontFamily, textTransform: 'none' }}
                    >
                        Manage payment methods
                    </Button>
                </Alert>
            )}
            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}
            <Button
                fullWidth variant="contained" onClick={handleConfirm}
                disabled={busy || !canConfirm}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ fontFamily, textTransform: 'none', fontWeight: 700, py: 1.25 }}
            >
                {busy
                    ? 'Processing...'
                    : isFree
                        ? `Continue with ${plan.display_name}`
                        : `Resubscribe to ${plan.display_name}`}
            </Button>
        </Box>
    );
};

// --- Step 2 (new card path): Stripe form + Save-this-card toggle + confirm. ----
// MUST be rendered inside <Elements> so PaymentElement / useStripe / useElements work.
const NewCardCheckout = ({ plan, billingCycle, onBack }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [saveCard, setSaveCard] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    const handleConfirm = async () => {
        if (busy) return;
        setBusy(true);
        setError(null);
        setInfo(null);
        try {
            if (!stripe || !elements) {
                setError('The payment form is still loading. Please wait a moment and try again.');
                return;
            }
            setInfo('Securing your card details...');
            const { error: stripeError, setupIntent } = await stripe.confirmSetup({
                elements, confirmParams: {}, redirect: 'if_required',
            });
            if (stripeError) {
                setError(stripeError.message || 'Please check your card details and try again.');
                setInfo(null);
                return;
            }
            const pmId = setupIntent?.payment_method;
            if (!pmId) {
                setError('Could not read the card details. Please try again.');
                setInfo(null);
                return;
            }
            setInfo('Processing your subscription...');
            const r = await postResubscription(plan.id, pmId, saveCard, billingCycle);
            if (r.ok) { window.location.href = '/chatbots'; return; }
            setError(r.message);
            setInfo(null);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
            setInfo(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box>
            <SelectedPlanSummary plan={plan} billingCycle={billingCycle} onBack={onBack} />
            <Card sx={{ borderRadius: '16px', mb: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
                <CardContent>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 1.5 }}>
                        Payment details
                    </Typography>
                    <PaymentElement options={{ layout: 'tabs' }} />
                    <FormControlLabel
                        sx={{ mt: 1.5 }}
                        control={<Checkbox checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} size="small" />}
                        label={(
                            <Typography sx={{ fontFamily, fontSize: '0.875rem', color: '#475569' }}>
                                Save this card for future use
                            </Typography>
                        )}
                    />
                </CardContent>
            </Card>
            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}
            {info && <Alert severity="info" sx={{ mb: 2, fontFamily }}>{info}</Alert>}
            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 1.5, textAlign: 'center' }}>
                Your card is charged only after you confirm below.
            </Typography>
            <Button
                fullWidth variant="contained" onClick={handleConfirm} disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ fontFamily, textTransform: 'none', fontWeight: 700, py: 1.25 }}
            >
                {busy ? 'Processing...' : `Resubscribe to ${plan.display_name}`}
            </Button>
        </Box>
    );
};

const Resubscribe = () => {
    const userStr = localStorage.getItem('user');
    let user = null;
    try { user = userStr ? JSON.parse(userStr) : null; } catch (e) { user = null; }
    const activeTeam = user?.active_team || null;
    const teamAccountType = activeTeam?.team_account_type || user?.account_type || 'individual';

    const [loading, setLoading] = useState(true);
    const [canResubscribe, setCanResubscribe] = useState(false);
    const [plans, setPlans] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    // Step-1 toggle. Default to monthly so first-time visitors see the lower
    // sticker price; they can flip to yearly to see the 10% saving.
    const [billingCycle, setBillingCycle] = useState('month');

    useEffect(() => {
        if (!user) { window.location.href = '/login'; }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const accessRes = await axiosInstance.get('/subscriptions/access-status');
            const access = accessRes.data || {};
            setCanResubscribe(!!access.can_resubscribe);

            // Already has access -> this page is not for them.
            if (['active', 'freemium', 'grace'].includes(access.state)) {
                window.location.href = '/chatbots';
                return;
            }

            if (access.can_resubscribe) {
                const [plansRes, pmRes] = await Promise.allSettled([
                    axiosInstance.get('/plans', { params: { account_type: teamAccountType } }),
                    axiosInstance.get('/payment-methods'),
                ]);
                if (plansRes.status === 'fulfilled') {
                    const raw = plansRes.value?.data;
                    const list = Array.isArray(raw) ? raw : (raw?.data || []);
                    setPlans(list.filter(p => !p.account_type || p.account_type === teamAccountType));
                }
                let pms = [];
                if (pmRes.status === 'fulfilled') {
                    const raw = pmRes.value?.data;
                    pms = Array.isArray(raw) ? raw : (raw?.data || []);
                    setPaymentMethods(pms);
                }
                // No usable card -> fetch a Stripe SetupIntent so the card form can render.
                const hasUsableCard = pms.some(pm => pm.is_usable !== false);
                if (!hasUsableCard && stripePromise) {
                    try {
                        const siRes = await axiosInstance.post('/payment-methods/setup-intent', {});
                        if (siRes.data?.client_secret) setClientSecret(siRes.data.client_secret);
                    } catch (e) {
                        console.error('SetupIntent fetch failed:', e);
                    }
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Could not load your billing details.');
        } finally {
            setLoading(false);
        }
    }, [teamAccountType]);

    useEffect(() => { loadData(); }, [loadData]);

    if (!user) return null;
    if (loading) return <Shell><CircularProgress /></Shell>;

    // --- Team member: cannot resubscribe ---
    if (!canResubscribe) {
        return (
            <Shell>
                <Card sx={{ maxWidth: 480, width: '100%', borderRadius: '20px', textAlign: 'center', p: 2 }}>
                    <CardContent>
                        <Box sx={{
                            width: 56, height: 56, borderRadius: '16px',
                            background: 'linear-gradient(135deg,#f59e0b,#d97706)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
                        }}>
                            <LockOutlinedIcon sx={{ color: '#fff', fontSize: 28 }} />
                        </Box>
                        <Typography sx={{ fontFamily, fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', mb: 1 }}>
                            Subscription expired
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.95rem', color: '#475569', mb: 3 }}>
                            Please contact your admin to resubscribe to a plan to regain access.
                        </Typography>
                        <Button onClick={logout} variant="outlined" sx={{ fontFamily, textTransform: 'none' }}>
                            Log out
                        </Button>
                    </CardContent>
                </Card>
            </Shell>
        );
    }

    // --- Admin / owner: can resubscribe ---
    const hasUsableCard = paymentMethods.some(pm => pm.is_usable !== false);
    const savedCard = paymentMethods.find(pm => pm.is_default && pm.is_usable !== false)
        || paymentMethods.find(pm => pm.is_usable !== false)
        || null;

    // Decide which Step 2 view to render based on the selected plan + saved-card state.
    let stepTwo = null;
    if (selectedPlan) {
        const isPaid = Number(selectedPlan.price) > 0;
        if (isPaid && !hasUsableCard) {
            // Paid plan with no saved card -> Stripe form path (must be inside <Elements>).
            stepTwo = clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                    <NewCardCheckout
                        plan={selectedPlan} billingCycle={billingCycle}
                        onBack={() => setSelectedPlan(null)}
                    />
                </Elements>
            ) : (
                <SavedCardCheckout
                    plan={selectedPlan} savedCard={null} billingCycle={billingCycle}
                    onBack={() => setSelectedPlan(null)}
                />
            );
        } else {
            // Free plan OR paid plan with a saved card -> straight to confirm.
            stepTwo = (
                <SavedCardCheckout
                    plan={selectedPlan} savedCard={savedCard} billingCycle={billingCycle}
                    onBack={() => setSelectedPlan(null)}
                />
            );
        }
    }

    return (
        <Shell>
            <Box sx={{ maxWidth: selectedPlan ? 640 : 1100, width: '100%' }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box sx={{
                        width: 56, height: 56, borderRadius: '16px',
                        background: 'linear-gradient(135deg,#3b82f6,#2563eb)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
                    }}>
                        <LockOutlinedIcon sx={{ color: '#fff', fontSize: 28 }} />
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>
                        {selectedPlan ? 'Confirm your plan' : 'Resubscribe to regain access'}
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.95rem', color: '#64748b', mt: 0.5 }}>
                        {selectedPlan
                            ? 'Review your selection and complete the resubscription below.'
                            : 'Your subscription has ended. Choose a plan below to continue, or switch to the free plan.'}
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

                {stepTwo || (
                    <PlanGrid
                        plans={plans} onSelect={setSelectedPlan}
                        billingCycle={billingCycle} onCycleChange={setBillingCycle}
                    />
                )}

                <Divider sx={{ my: 3 }} />
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ flexWrap: 'wrap' }}>
                    <Button
                        size="small"
                        onClick={() => { window.location.href = '/Billing?tab=invoices'; }}
                        sx={{ fontFamily, textTransform: 'none' }}
                    >
                        View invoices
                    </Button>
                    <Button size="small" onClick={logout} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Log out
                    </Button>
                </Stack>
            </Box>
        </Shell>
    );
};

export default Resubscribe;
